import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import CryptoJS from "crypto-js";
import buildHasuraProvider from 'ra-data-hasura';
import { useEffect, useState } from "react";
import { CREATE, DataProvider, DELETE, DELETE_MANY, GET_LIST, GET_MANY, GET_MANY_REFERENCE, GET_ONE, UPDATE, UPDATE_MANY } from "react-admin";
import AnonymousDataProvider from '../auth/AnonymousDataProvider';
import { REACT_APP_BACK_PATH, REACT_APP_ENCRYPT_PASSPHRASE } from '../auth/AuthProvider';
import { init } from "./Common";

const format = "hasura-raw-query";

const convertFileToBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file.rawFile);
    });

const HasuraSchemaProvider = (hasura): DataProvider => {
    const handle = async (type, resource, params): Promise<any> => {
        if (params && params.filter) {
            for (const key of Object.keys(params.filter)) {
                const value = params.filter[key];
                if (key.startsWith("@")) {
                    delete params.filter[key];
                    params.filter[key.substring(1)] = {
                        format,
                        value
                    };
                }
            }
        }

        if (type === UPDATE || type === CREATE) {
            if (params.data.q) {
                delete params.data['q'];
            }
            if (resource === "user") {
                if (params.data.newAvatar) {
                    params.data.avatar = await convertFileToBase64(params.data.newAvatar);
                    delete params.data['newAvatar'];
                }
                if (params.data.newPassword) {
                    params.data.password = CryptoJS.HmacSHA512(params.data.newPassword, REACT_APP_ENCRYPT_PASSPHRASE).toString(CryptoJS.enc.Base64);
                    delete params.data['newPassword'];
                }
            } else if (resource === "stage_infos") {
                if (params.data.isMultipleTeam) {
                    params.data['assignId'] = null;
                    if (typeof params.data.assignIds !== 'string') {
                        params.data.assignIds = `{${params.data.assignIds.join(",")}}`;
                    }
                } else {
                    params.data['assignIds'] = "{}";
                }
            }
        }
        return await hasura(type, `geoprod_${resource}`, params);
    };
    return {
        getList: (resource, params) => handle(GET_LIST, resource, params),
        getOne: (resource, params) => handle(GET_ONE, resource, params),
        getMany: (resource, params) => handle(GET_MANY, resource, params),
        getManyReference: (resource, params) => handle(GET_MANY_REFERENCE, resource, params),
        update: (resource, params) => handle(UPDATE, resource, params),
        updateMany: (resource, params) => handle(UPDATE_MANY, resource, params),
        create: (resource, params) => handle(CREATE, resource, params),
        delete: (resource, params) => handle(DELETE, resource, params),
        deleteMany: (resource, params) => handle(DELETE_MANY, resource, params),
    };
};



export const useHasura = (token) => {
    const [dataProvider, setDataProvider] = useState(null);
    const [isAnonyme, setAnonyme] = useState(true);
    useEffect(() => {
        if (!token) {
            // this allow see login page even if no backoffice and allow to get dataProvider only and only if we are authentificated
            setAnonyme(true);
            setDataProvider(AnonymousDataProvider);
        } else {
            if (isAnonyme) {
                setDataProvider(null); // we set to null to show the loader
            }
            const buildDataProvider = async () => {
                const link = createHttpLink({
                    uri: `${REACT_APP_BACK_PATH}/api`,
                    headers: {
                        authorization: `Bearer ${token}`
                    },
                    fetchOptions: {
                        mode: 'cors'
                    }
                });
                const myClientWithAuth = await new ApolloClient({
                    link,
                    cache: new InMemoryCache()
                });
                const hasuraProvider = await buildHasuraProvider({
                    client: myClientWithAuth,
                }, {
                    buildFields: (type, fetchType) => {
                        return init(type, fetchType);
                    }
                });
                setAnonyme(false);
                setDataProvider(HasuraSchemaProvider(hasuraProvider));
            };
            buildDataProvider();
        }
    }, [token, isAnonyme]);
    return dataProvider;
};
